import { useState, useEffect, useRef } from 'react';
import styles from './multi-select-input.module.scss';

interface MultiSelectInputProps<T extends Record<string, any>> {
  options: T[];
  inputTitle?: string;
  selectedOptions: T[];
  onChange: (selected: T[]) => void;
  placeholder?: string;
  noOptionsMessage?: string;
  displayField?: keyof T; // Поле для отображения (по умолчанию 'label')
  getOptionId?: (option: T) => string | number; // Функция для получения ID (опционально)
}

export const MultiSelectInput = <T extends Record<string, any>>({
  options,
  inputTitle = 'Заголовок',
  selectedOptions = [],
  onChange,
  placeholder = 'Выберите...',
  noOptionsMessage = 'Нет вариантов',
  displayField = 'label' as keyof T,
  getOptionId = (option) => option.id, // По умолчанию ищем поле 'id'
}: MultiSelectInputProps<T>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Фильтрация опций (исключая выбранные)
  const filteredOptions = options.filter(
    (option) =>
      String(option[displayField])
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) &&
      !selectedOptions.some(
        (selected) => getOptionId(selected) === getOptionId(option)
      )
  );

  // Выбор элемента
  const handleSelect = (option: T) => {
    onChange([...selectedOptions, option]);
    setSearchTerm('');
  };

  // Удаление выбранного элемента
  const handleRemove = (id: string | number) => {
    onChange(selectedOptions.filter((item) => getOptionId(item) !== id));
  };

  // Закрытие дропдауна при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.multi_select_input__wrapper}>
      <div className={styles.multi_select_input__title}>{inputTitle}</div>
      <div className={styles.multi_select_input} ref={dropdownRef}>
        <div
          className={styles.input__container}
          onClick={() => setIsDropdownOpen(true)}
        >
          {selectedOptions.length > 0 && (
            <div className={styles.selected_tags}>
              {selectedOptions.map((option) => (
                <span key={getOptionId(option)} className={styles.tag}>
                  {option[displayField]}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(getOptionId(option));
                    }}
                    className={styles.remove_tag}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={placeholder}
            onFocus={() => setIsDropdownOpen(true)}
          />
        </div>

        {/* Выпадающий список */}
        {isDropdownOpen && (
          <div className={styles.dropdown}>
            {filteredOptions.length === 0 ? (
              <div className={(styles.dropdown__item, styles.no_options)}>
                {noOptionsMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={getOptionId(option)}
                  className={styles.dropdown__item}
                  onClick={() => handleSelect(option)}
                >
                  {option[displayField]}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
